/**
 * CS 代行の受け取り口。
 *
 * オペレーター向けアプリが開いた窓がこの画面で、**このオリジンの窓の中でだけ**
 * 委任トークンを受け取る。認可サーバーへ POST して期限付きセッションを作り、
 * PKCE のコード交換後にホームへ進む。トークンはブラウザーの Storage に残さない。
 *
 * ## 受け取る相手を名指しする
 *
 * `VITE_ADMIN_OPERATOR_APP_ORIGIN` に一致する送り元からのメッセージだけを読む。
 * 未設定の面ではこの画面は**何も受け取らない**(繋がっていない口を、繋がって
 * いるように見せない)。
 *
 * ## トークンを画面に出さない
 *
 * 受け取った値は state にも URL にも出さない。失敗しても理由だけを出す。
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  DELEGATION_HANDOFF_READY,
  DELEGATION_HANDOFF_TIMEOUT_MS,
  ErrorState,
  LoadingState,
  readDelegationHandoffToken,
  useSession,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/HelpIcon";

/** 受け取ってよい送り元。ビルド時に決まる。 */
function operatorOrigin(): string {
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  return env["VITE_ADMIN_OPERATOR_APP_ORIGIN"] ?? "";
}

export function DelegationHandoffPage() {
  const session = useSession();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const origin = operatorOrigin();
    if (origin === "") {
      setError("この環境では代行の受け渡しが有効になっていません。");
      return;
    }
    const opener = window.opener as Window | null;
    if (opener === null) {
      setError("代行の受け渡し元が見つかりません。もう一度お試しください。");
      return;
    }

    let received = false;
    const timer = window.setTimeout(() => {
      received = true;
      setError("代行の受け渡しが時間内に終わりませんでした。");
    }, DELEGATION_HANDOFF_TIMEOUT_MS);

    const onMessage = (event: MessageEvent): void => {
      // 送り元のオリジンが一致しないメッセージは読まない
      if (event.origin !== origin || event.source !== opener || received)
        return;
      const token = readDelegationHandoffToken(event.data);
      if (token === null) return;
      received = true;
      window.clearTimeout(timer);
      void session
        .startDelegatedBrowserSession({
          accessToken: token.accessToken,
          expiresIn: token.expiresIn,
        })
        .then(() => {
          if (session.snapshot().status === "authenticated")
            void navigate("/", { replace: true });
        })
        .catch(() => {
          setError(
            "代行の準備ができませんでした。代行を開始し直してください。",
          );
        });
    };

    window.addEventListener("message", onMessage);
    opener.postMessage({ type: DELEGATION_HANDOFF_READY }, origin);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("message", onMessage);
    };
  }, [navigate, session]);

  return (
    <div className="vd-page">
      {/* 見出しが無い画面なので、状態の上に置く */}
      <div className="vd-row" style={{ justifyContent: "flex-end" }}>
        <HelpIcon slug="account/delegation" />
      </div>
      {error === null ? (
        <LoadingState label="代行の準備をしています" />
      ) : (
        <ErrorState description={error} />
      )}
    </div>
  );
}
